<template>
  <div
    class="microcart cl-mine-shaft relative"
    :class="[productsInCart.length ? 'bg-cl-secondary' : 'bg-cl-primary']"
    data-testid="microcart"
    style="min-height: 100%"
  >
    <LoaderScoped v-if="cartSyncing" />
    <transition name="fade">
      <div v-if="isEditMode" class="overlay" @click="closeEditMode" />
    </transition>
    <div class="row bg-cl-primary px40 actions">
      <div class="col-xs end-xs">
        <button
          type="button"
          class="p0 brdr-none bg-cl-transparent close"
          @click="closeMicrocartExtend"
          data-testid="closeMicrocart"
        >
          <i class="material-icons close-window py20 cl-mine-shaft"> close </i>
        </button>
      </div>
    </div>
    <div class="row middle-xs bg-cl-primary top-sm px40 actions">
      <div class="col-xs-12 col-sm">
        <h2 v-if="productsInCart.length" class="cl-mine-shaft mt35 mb35">
          {{ $t("Shopping cart") }}
        </h2>
      </div>
      <div class="col-xs-12 col-sm mt35 mb35 end-sm clearcart-col">
        <clear-cart-button
          v-if="productsInCart.length"
          @click.native="clearCart"
        />
      </div>
    </div>

    <h4 v-if="!productsInCart.length" class="cl-mine-shaft ml30">
      {{ $t("Your shopping cart is empty.") }}
    </h4>
    <div
      v-if="!productsInCart.length"
      class="ml30"
      @click="closeMicrocartExtend"
    >
      {{ $t("Don't hesitate and") }}
      <router-link :to="localizedRoute('/')">
        {{ $t("browse our catalog") }}
      </router-link>
      {{ $t("to find something beautiful for You!") }}
    </div>
    <ul
      v-if="productsInCart.length"
      class="bg-cl-primary m0 px40 pb40 products"
    >
      <product
        v-for="product in productsInCart"
        :key="product.server_item_id || product.id"
        :product="product"
        :categoryProps="categoryProps"
        :productsInCart="productsInCart"
      />
    </ul>
    <div
      v-if="productsInCart.length"
      class="summary px40 cl-mine-shaft sans-serif"
    >
      <h3 class="m0 pt40 mb30 weight-400 summary-heading">
        {{ $t("Shopping summary") }}
      </h3>
      <div
        v-for="(segment, index) in totals"
        :key="index"
        class="row py10 px10"
        v-if="segment.code !== 'grand_total'"
      >
        <div class="col-xs" v-if="segment.code === 'shipping'">
          {{ $t(segment.title) }}
        </div>
        <div class="col-xs" v-else-if="segment.code === 'discount'">
          {{ getDiscountName || $t(segment.title) }}
        </div>
        <div class="col-xs" v-else>
          {{ $t(segment.title) }}
        </div>
        <div v-if="segment.value != null" class="col-xs align-right">
          {{ segment.value | price(storeView) }}
        </div>
      </div>
      <div class="row py20 px10">
        <div
          v-if="OnlineOnly && !addCouponPressed && !isCouponApplied"
          class="col-xs-12"
        >
          <button
            class="p0 py10 cl-black brdr-1 brdr-bg-black sans-serif fs-medium bg-cl-transparent return-btn"
            type="button"
            @click="addDiscountCoupon"
          >
            {{ $t("Add a discount code") }}
          </button>
        </div>
        <div
          v-if="OnlineOnly && addCouponPressed && !isCouponApplied"
          class="col-xs-12 pt30 coupon-wrapper"
        >
          <div class="coupon-input">
            <label class="h6 cl-mine-shaft">{{ $t("Discount code") }}</label>
            <base-input
              type="text"
              id="couponinput"
              :autofocus="true"
              v-model.trim="couponCode"
              @keyup.enter="setCoupon"
            />
          </div>
          <button-outline
            color="dark"
            class="mt20"
            :disabled="!couponCode"
            @click.native="setCoupon"
          >
            {{ $t("Add discount code") }}
          </button-outline>
        </div>
        <div v-if="OnlineOnly && isCouponApplied" class="col-xs-12 col-sm-4 p0">
          <button-full
            :active-color="true"
            :round="true"
            @click.native="deleteCoupon"
          >
            {{ $t("Delete discount code") }}
          </button-full>
        </div>
      </div>

      <div
        class="row pt30 pb20 px10 weight-700 middle-xs"
        v-for="(segment, index) in totals"
        :key="index"
        v-if="segment.code === 'grand_total'"
      >
        <div class="col-xs h4 total-price-label">
          {{ $t(segment.title) }}
        </div>
        <div class="col-xs align-right h2 total-price-value">
          {{ segment.value | price(storeView) }}
          <p
            class="total-price-value--net cl-mine-shaft"
            v-if="cartGrandNetTotal"
          >
            {{ cartGrandNetTotal | price(storeView) }} {{ $t("net") }}
          </p>
        </div>
      </div>
    </div>

    <div class="px40" v-if="productsInCart.length && !isCheckoutMode">
      <div class="free-shipping mb20 mt20 px10">
        <div class="free-shipping__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="49" height="26">
            <path
              fill-rule="evenodd"
              fill="#51028F"
              d="M47.113 15.062h-9.548v4.982h1.6c.744-1.031 1.887-1.718 3.202-1.718 1.258 0 2.402.63 3.145 1.718h1.601v-4.982zm-38.537-.287c.515 0 .972.458.972.974 0 .515-.457.973-.972.973H4.745c-.514 0-.914-.458-.914-.973 0-.516.4-.974.914-.974h3.831zm0-4.753c.515 0 .972.401.972.973 0 .516-.457.917-.972.917H2.859c-.515 0-.972-.401-.972-.917 0-.572.457-.973.972-.973h5.717zm0-4.754c.515 0 .972.401.972.917 0 .515-.457.973-.972.973H.972A.964.964 0 010 6.185c0-.516.4-.917.972-.917h7.604zm29.96 16.666H26.129C26.244 24.11 24.528 26 22.356 26c-2.23 0-3.945-1.89-3.831-4.066h-4.746a.965.965 0 01-.972-.974V.973c0-.572.458-.973.972-.973H36.65c.514 0 .915.401.915.973v4.754l4.688-.058c.343 0 .629.115.857.401l5.661 7.445c.171.115.229.344.229.63v6.815c0 .573-.401.974-.972.974h-1.887C46.255 24.11 44.54 26 42.367 26c-2.23 0-3.945-1.89-3.831-4.066zm3.831-1.661c-1.086 0-1.944.859-1.944 1.89s.858 1.89 1.944 1.89a1.869 1.869 0 001.887-1.89c0-1.031-.858-1.89-1.887-1.89zM25.5 20.044h10.178V1.89H14.694v18.154h4.517c.686-1.031 1.83-1.718 3.145-1.718 1.257 0 2.458.687 3.144 1.718zm-3.144.229c-1.087 0-1.944.859-1.944 1.89 0 1.088.857 1.89 1.944 1.89a1.868 1.868 0 001.886-1.89c0-1.031-.857-1.89-1.886-1.89zM37.565 7.617v5.555h8.576l-4.288-5.613-4.288.058z"
            />
          </svg>
        </div>
        <div class="free-shipping__text">
          <template v-if="cartGrandTotal < freeShippingMinValue">
            <p class="weight-700 mb5 mt0">
              Do darmowej dostawy brakuje:
              <span class="nowrap">{{
                (freeShippingMinValue - cartGrandTotal) | price
              }}</span>
            </p>
            <p class="h6 m0">
              Darmowa dostawa od: {{ freeShippingMinValue | price }}
            </p>
          </template>
          <template v-else>
            <p class="weight-700 mb5 mt0 h4">Darmowa dostawa!</p>
          </template>
        </div>
      </div>
    </div>

    <div
      class="row py20 px40 middle-xs actions"
      v-if="productsInCart.length && !isCheckoutMode"
    >
      <div class="col-xs-12 col-sm-4 first-sm">
        <router-link
          :to="localizedRoute('/')"
          class="fs-medium py10 no-underline cl-black link brdr-1 brdr-bg-black return-btn"
        >
          <span @click="closeMicrocartExtend">
            {{ $t("Return to shopping") }}
          </span>
        </router-link>
      </div>
      <div class="col-xs-12 col-sm-3 first-sm hidden-xs"></div>
      <div class="col-xs-12 first-xs col-sm-4 end-sm">
        <button-full
          :categoryProps="categoryProps"
          :link="{ name: 'checkout' }"
          :active-color="true"
          :round="true"
          @click.native="goToCheckout"
        >
          {{ $t("Go to checkout") }}
          <i class="material-icons ml10">shopping_cart</i>
        </button-full>
        <!-- <instant-checkout v-if="isInstantCheckoutRegistered" class="no-outline button-full block brdr-none w-100 px10 py20 bg-cl-mine-shaft :bg-cl-th-secondary ripple weight-400 h4 cl-white sans-serif fs-medium mt20" /> -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import i18n from "@vue-storefront/i18n";
import { isModuleRegistered } from "@vue-storefront/core/lib/modules";
import { currentStoreView } from "@vue-storefront/core/lib/multistore";

import VueOfflineMixin from "vue-offline/mixin";
import onEscapePress from "@vue-storefront/core/mixins/onEscapePress";
import InstantCheckout from "src/modules/instant-checkout/components/InstantCheckout.vue";
import { registerModule } from "@vue-storefront/core/lib/modules";

import BaseInput from "theme/components/core/blocks/Form/BaseInput";
import ClearCartButton from "theme/components/core/blocks/Microcart/ClearCartButton";
import ButtonFull from "theme/components/theme/ButtonFull";
import ButtonOutline from "theme/components/theme/ButtonOutline";
import CmsBlockChunk from "theme/components/core/blocks/Cms/BlockChunk";
import Product from "theme/components/core/blocks/Microcart/Product";
import EditMode from "./EditMode";
import { InstantCheckoutModule } from "src/modules/instant-checkout";
import LoaderScoped from "theme/components/core/LoaderScoped.vue";
import config from 'config'

export default {
  data () {
    return {
      config
    }
  },
  components: {
    Product,
    ClearCartButton,
    ButtonFull,
    ButtonOutline,
    BaseInput,
    InstantCheckout,
    CmsBlockChunk,
    LoaderScoped,
  },
  mixins: [VueOfflineMixin, EditMode, onEscapePress],
  data() {
    return {
      addCouponPressed: false,
      couponCode: "",
      componentLoaded: false,
      isInstantCheckoutRegistered: isModuleRegistered("InstantCheckoutModule"),
    };
  },
  props: {
    isCheckoutMode: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    categoryProps: {
      type: String,
      default: config.rootcategories.drukowanie
    }
  },
  beforeCreate() {
    registerModule(InstantCheckoutModule);
  },
  created() {
    this.$store.dispatch("cmsBlock/single", {
      key: "identifier",
      value: "c_CARD_FREE_DELIVERY",
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.componentLoaded = true;
    });
  },
  computed: {
    ...mapGetters({
      productsInCart: "cart/getCartItems",
      appliedCoupon: "cart/getCoupon",
      totals: "cart/getTotals",
      isOpen: "cart/getIsMicroCartOpen",
    }),
    cartSyncing() {
      return this.$store.getters[`cart/isTotalsSyncRequired`];
    },
    getDiscountName() {
      if (this.productsInCart.length > 1) {
        const discountItem = this.productsInCart.find(
          (item) => item.item_id === 0
        );
        if (discountItem) {
          return discountItem.name;
        }
      }
      return "";
    },
    storeView() {
      return currentStoreView();
    },
    freeShippingMinValue() {
      try {
        //
        let freeDelivery = this.$store.getters[`cmsBlock/cmsBlockIdentifier`](
          "c_CARD_FREE_DELIVERY"
        );

        if (freeDelivery) {
          let content = JSON.parse(freeDelivery.content);

          if (typeof content.pl !== "undefined") {
            return !isNaN(parseInt(content.pl)) ? parseInt(content.pl) : 300;
          } else {
            return !isNaN(parseInt(content)) ? parseInt(content) : 300;
          }
        }
      } catch (e) {
        return 300;
      }

      return 300;
    },
    cartGrandNetTotal() {
      const netTotal = this.totals.find(
        (total) => total.code === "grand_net_total"
      );

      if (netTotal) {
        return netTotal.value;
      }

      return false;
    },
    cartGrandTotal() {
      const netTotal = this.totals.find(
        (total) => total.code === "grand_total"
      );

      if (netTotal) {
        return netTotal.value;
      }

      return false;
    },
    unitTotal() {
      const unitTotal = this.totals.find(
        (total) => total.code === "unit_total"
      );

      if (unitTotal) {
        return unitTotal.value;
      }

      return false;
    },
    isCouponApplied() {
      const discountTotal = this.totals.find(
        (total) => total.code === "discount"
      );
      if (discountTotal && discountTotal.value) {
        return +discountTotal.value < 0;
      }
      return false;
    },
  },
  methods: {
    ...mapActions({
      applyCoupon: "cart/applyCoupon",
      removeCoupon: "cart/removeCoupon",
    }),
    addDiscountCoupon() {
      this.addCouponPressed = true;
    },
    clearCoupon() {
      this.$store.dispatch("cart/removeCoupon");
      this.addCouponPressed = false;
    },
    async setCoupon() {
      const couponApplied = await this.applyCoupon(this.couponCode);
      this.addCouponPressed = false;
      this.couponCode = "";
      if (!couponApplied) {
        this.$store.dispatch("notification/spawnNotification", {
          type: "warning",
          message: i18n.t(
            "You've entered an incorrect coupon code. Please try again."
          ),
          action1: { label: i18n.t("OK") },
        });
      }
    },
    async deleteCoupon() {
      const couponDeleted = await this.removeCoupon();
      if (!couponDeleted) {
        this.$store.dispatch("notification/spawnNotification", {
          type: "warning",
          message: i18n.t("Please try again."),
          action1: { label: i18n.t("OK") },
        });
      }
    },
    closeMicrocartExtend() {
      this.$store.commit("ui/setMicrocart", false);
      this.$store.commit("ui/setSidebar", false);
      this.addCouponPressed = false;
    },
    async goToCheckout(e) {
      e.preventDefault();
      const itemsAvailable = await this.$store.dispatch(
        "cart/checkItemsAvailability"
      );
      console.log("item available ====", itemsAvailable);

      if (itemsAvailable) {
        this.$router.push({ name: "checkout" });
        this.closeMicrocartExtend();
      }
    },
    onEscapePress() {
      this.$store.dispatch("ui/closeMicrocart");
    },
    clearCart() {
      this.$store.dispatch("notification/spawnNotification", {
        type: "warning",
        message: i18n.t(
          "Are you sure you would like to remove all the items from the shopping cart?"
        ),
        action1: { label: i18n.t("Cancel"), action: "close" },
        action2: {
          label: i18n.t("OK"),
          action: async () => {
            // We just need to clear cart on frontend and backend.
            // but cart token can be reused
            await this.$store.dispatch("cart/clear", { disconnect: false });
          },
        },
        hasNoTimeout: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~theme/css/animations/transitions";
@import "~theme/css/variables/colors";

.close {
  i {
    opacity: 0.6;
  }
  &:hover,
  &:focus {
    i {
      opacity: 1;
    }
  }
}

.return-btn {
  border-radius: 24px;
  min-width: 250px;
  display: flex;
  justify-content: center;
}

.mt0 {
  @media (max-width: 767px) {
    margin-top: 0;
  }
}

.clearcart {
  &-col {
    display: flex;
    align-self: center;
  }
}

.products {
  @media (max-width: 767px) {
    padding: 30px 15px;
  }
}

.actions {
  @media (max-width: 768px) {
    padding: 0 15px;
  }
  .link {
    @media (max-width: 768px) {
      display: flex;
      justify-content: center;
      box-sizing: border-box;
      padding: 10px 70px;
      margin-top: 10px;
      margin-bottom: 10px;

      &.checkout {
        margin-top: 55px;
        padding: 0;
      }
    }
  }
}

.summary {
  @media (max-width: 767px) {
    padding: 0 15px;
    font-size: 12px;
  }
}

.free-shipping {
  padding: 15px;
  border: 1px solid $indigo-light;
  border-radius: 7px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (min-width: 401px) {
    padding: 15px 20px;
  }

  &__icon {
    @media (max-width: 400px) {
      display: none;
    }
  }

  &__text {
    @media (min-width: 401px) {
      padding-left: 20px;
      text-align: right;
    }

    .weight-700 {
      color: $indigo-light;
    }
  }
}

.summary-heading {
  @media (max-width: 767px) {
    font-size: 18px;
  }
}

.total-price-label {
  @media (max-width: 767px) {
    font-size: 18px;
  }
}

.total-price-value {
  @media (max-width: 767px) {
    font-size: 24px;
  }

  &--net {
    font-size: 16px;
    font-weight: 400;
    margin: 10px 0 0;
  }
}

.delete-button {
  vertical-align: middle;
}

.coupon-wrapper {
  display: flex;

  .button-outline {
    text-transform: inherit;
    width: 50%;
  }

  .coupon-input {
    margin-right: 20px;
    width: 100%;
  }
}

.overlay {
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  z-index: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.close-window {
  position: absolute;
  top: 0;
  right: 33px;
  z-index: 9;
}
</style>
